import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "picker", "highlight"]
  static values = {
    config: Object
  }

  connect() {
    this.selectedIndex = 0
    this.currentMode = null // 'command', 'modifier', or 'value'
    this.currentModifier = null
    this.updateHighlight()
  }

  // Called on input changes
  checkInput(event) {
    const value = event.target.value
    const cursorPos = event.target.selectionStart
    
    this.updateHighlight()
    this.detectContext(value, cursorPos)
  }

  // Detect what type of autocomplete to show
  detectContext(value, cursorPos) {
    const textBeforeCursor = value.substring(0, cursorPos)
    
    // Check for modifier value context: --modifier <value>
    const modifierValueMatch = textBeforeCursor.match(/--(size|quality|style|model)\s+(\S*)$/i)
    if (modifierValueMatch && this.isImageCommand(value)) {
      this.showValuePicker(modifierValueMatch[1].toLowerCase(), modifierValueMatch[2])
      return
    }

    // Check for modifier context: -- at end
    const modifierMatch = textBeforeCursor.match(/--(\S*)$/i)
    if (modifierMatch && this.isImageCommand(value)) {
      this.showModifierPicker(modifierMatch[1])
      return
    }

    // Check for command context: / at start
    const commandMatch = textBeforeCursor.match(/^\/(\S*)$/i)
    if (commandMatch) {
      this.showCommandPicker(commandMatch[1])
      return
    }

    this.hidePicker()
  }

  isImageCommand(value) {
    return /^\/(imagine|paint)\b/i.test(value)
  }

  showCommandPicker(filter) {
    this.currentMode = 'command'
    const commands = this.configValue.commands || []
    const filtered = commands.filter(cmd => 
      cmd.trigger.toLowerCase().includes(`/${filter.toLowerCase()}`)
    )
    this.renderPicker(filtered, 'commands')
  }

  showModifierPicker(filter) {
    this.currentMode = 'modifier'
    const modifiers = this.configValue.modifiers?.image || []
    const filtered = modifiers.filter(mod =>
      mod.trigger.toLowerCase().includes(`--${filter.toLowerCase()}`)
    )
    this.renderPicker(filtered, 'modifiers')
  }

  showValuePicker(modifier, filter) {
    this.currentMode = 'value'
    this.currentModifier = modifier
    const modifiers = this.configValue.modifiers?.image || []
    const mod = modifiers.find(m => m.trigger === `--${modifier}`)
    if (!mod || !mod.values) {
      this.hidePicker()
      return
    }
    
    const values = mod.values
      .filter(v => v.toLowerCase().includes(filter.toLowerCase()))
      .map(v => ({ trigger: v, description: '' }))
    this.renderPicker(values, 'values')
  }

  renderPicker(items, category) {
    if (!this.hasPickerTarget || items.length === 0) {
      this.hidePicker()
      return
    }

    this.selectedIndex = 0
    
    const html = `
      <div role="listbox" class="max-h-48 overflow-y-auto py-1">
        ${items.map((item, index) => this.renderOption(item, index)).join('')}
      </div>
    `
    
    this.pickerTarget.innerHTML = html
    this.pickerTarget.classList.remove('hidden')
    this.items = items
  }

  renderOption(item, index) {
    const isSelected = index === this.selectedIndex
    const selectedClass = isSelected ? 'bg-zinc-100 dark:bg-zinc-700' : ''
    
    return `
      <div role="option" 
           data-index="${index}"
           data-action="click->slash-command#selectOption mouseenter->slash-command#hoverOption"
           class="px-3 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 cursor-pointer grid grid-cols-[auto_1fr] gap-3 items-center ${selectedClass}"
           aria-selected="${isSelected}">
        <span class="font-mono font-medium text-primary-500">${this.escapeHtml(item.trigger)}</span>
        <span class="text-zinc-500 dark:text-zinc-400 text-sm truncate">${this.escapeHtml(item.description || '')}</span>
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  hidePicker() {
    if (this.hasPickerTarget) {
      this.pickerTarget.classList.add('hidden')
    }
    this.currentMode = null
    this.items = []
  }

  // Keyboard navigation
  handleKeydown(event) {
    if (!this.currentMode || !this.items?.length) {
      // Default behavior: submit on Enter
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault()
        event.target.form.requestSubmit()
      }
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.items.length - 1)
        this.updateSelection()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateSelection()
        break
      case 'Enter':
      case 'Tab':
        event.preventDefault()
        this.selectCurrentItem()
        break
      case 'Escape':
        event.preventDefault()
        this.hidePicker()
        break
    }
  }

  updateSelection() {
    if (!this.hasPickerTarget) return
    
    const options = this.pickerTarget.querySelectorAll('[role="option"]')
    options.forEach((opt, idx) => {
      const isSelected = idx === this.selectedIndex
      opt.classList.toggle('bg-zinc-100', isSelected)
      opt.classList.toggle('dark:bg-zinc-700', isSelected)
      opt.setAttribute('aria-selected', isSelected)
    })
    
    // Scroll into view
    options[this.selectedIndex]?.scrollIntoView({ block: 'nearest' })
  }

  hoverOption(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (!isNaN(index)) {
      this.selectedIndex = index
      this.updateSelection()
    }
  }

  selectOption(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (!isNaN(index)) {
      this.selectedIndex = index
      this.selectCurrentItem()
    }
  }

  selectCurrentItem() {
    if (!this.items?.length || this.selectedIndex >= this.items.length) return
    
    const item = this.items[this.selectedIndex]
    const input = this.inputTarget
    const value = input.value
    const cursorPos = input.selectionStart

    let newValue, newCursorPos

    switch (this.currentMode) {
      case 'command':
        // Replace everything from / to cursor with the command
        newValue = item.trigger + ' '
        newCursorPos = newValue.length
        break
        
      case 'modifier':
        // Replace --partial with full modifier
        const modMatch = value.substring(0, cursorPos).match(/--\S*$/)
        if (modMatch) {
          const beforeModifier = value.substring(0, cursorPos - modMatch[0].length)
          const afterCursor = value.substring(cursorPos)
          newValue = beforeModifier + item.trigger + ' ' + afterCursor.trimStart()
          newCursorPos = beforeModifier.length + item.trigger.length + 1
        }
        break
        
      case 'value':
        // Replace partial value with full value
        const valMatch = value.substring(0, cursorPos).match(/--(size|quality|style|model)\s+\S*$/i)
        if (valMatch) {
          const modifierEnd = value.substring(0, cursorPos).lastIndexOf(valMatch[0]) + valMatch[0].indexOf(' ') + 1
          const beforeValue = value.substring(0, modifierEnd)
          const afterCursor = value.substring(cursorPos)
          newValue = beforeValue + item.trigger + ' ' + afterCursor.trimStart()
          newCursorPos = beforeValue.length + item.trigger.length + 1
        }
        break
    }

    if (newValue !== undefined) {
      input.value = newValue
      input.setSelectionRange(newCursorPos, newCursorPos)
      this.updateHighlight()
    }

    this.hidePicker()
    input.focus()
  }

  // Syntax highlighting
  updateHighlight() {
    if (!this.hasHighlightTarget || !this.hasInputTarget) return
    
    const value = this.inputTarget.value
    const hasSlashCommand = /^\/\S+/.test(value)
    
    if (hasSlashCommand) {
      // Use overlay for syntax highlighting
      const highlighted = this.highlightSyntax(value)
      this.highlightTarget.innerHTML = highlighted + '\n'
      this.highlightTarget.classList.remove('hidden')
      this.inputTarget.classList.add('text-transparent')
      this.inputTarget.classList.remove('text-zinc-900', 'dark:text-zinc-100')
    } else {
      // No slash command - use normal text rendering
      this.highlightTarget.innerHTML = ''
      this.highlightTarget.classList.add('hidden')
      this.inputTarget.classList.remove('text-transparent')
      this.inputTarget.classList.add('text-zinc-900', 'dark:text-zinc-100')
    }
    
    // Sync scroll position
    this.highlightTarget.scrollTop = this.inputTarget.scrollTop
    this.highlightTarget.scrollLeft = this.inputTarget.scrollLeft
  }

  highlightSyntax(text) {
    if (!text) return ''
    
    // Escape HTML
    let result = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    
    // Highlight commands at start: /imagine, /paint, /moneybird, etc.
    result = result.replace(
      /^(\/\S+)/,
      '<span class="text-primary-500">$1</span>'
    )
    
    // Highlight modifiers and their values: --size 1024x1024
    result = result.replace(
      /(--(?:size|quality|style|model))(?:\s+(\S+))?/gi,
      (match, modifier, value) => {
        let html = `<span class="text-blue-500">${modifier}</span>`
        if (value) {
          html += ` <span class="text-emerald-500">${value}</span>`
        }
        return html
      }
    )
    
    return result
  }

  // Auto-resize textarea
  autoResize(event) {
    const textarea = event.target
    textarea.style.height = "auto"
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px"
    
    // Sync highlight height
    if (this.hasHighlightTarget) {
      this.highlightTarget.style.height = textarea.style.height
    }
  }

  // Sync scroll between textarea and highlight overlay
  syncScroll(event) {
    if (this.hasHighlightTarget) {
      this.highlightTarget.scrollTop = event.target.scrollTop
      this.highlightTarget.scrollLeft = event.target.scrollLeft
    }
  }
}
